import { Selectors } from "../../Everou/_reducers/app.reducer";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    CapsuleButton,
    ContentSpace,
    MainPageWithButtons,
} from "../../Common/_components";

import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import { PMSCustomizationActions } from "../_actions/PMSCustomizationActions";
import { PMSOrganizationCustomizationActions } from "../_actions/PMSOrganizationCustomizationActions";
import { CustomizationTemplateEditor } from "../_components/CustomizationTemplateEditor";
import { PMSOrganizationScopeSelector } from "../_components/PMSOrganizationScopeSelector";
import { useActiveOrganization } from "../_helpers/useActiveOrganization";

type Props = {
    // Mounted inside the organization's Customization tab: the tab already
    // names the organization and draws the card.
    isEmbedded?: boolean;
};

export function PMSCustomizationModule({ isEmbedded = false }: Props = {}) {
    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Organization-centric (BAC-215): members edit their organization's
    // template; the personal one is the legacy fallback without organization.
    const { activeOrganization, isResolving, hasFailed } =
        useActiveOrganization();
    const activeUid = activeOrganization?.uid ?? null;

    //GLOBAL STATE
    const personalCustomization = useSelector(
        Selectors.getPMSCustomizationTemplate,
    );
    const personalIsUploading = useSelector(
        Selectors.getPMSCustomizationTemplateIsUploading,
    );
    const storedOrganizationTemplate = useSelector(
        Selectors.getOrganizationCustomizationTemplate,
    );
    const organizationIsUploading = useSelector(
        Selectors.getOrganizationCustomizationIsUploading,
    );

    useEffect(() => {
        if (isResolving) return;
        if (activeUid) {
            dispatch(
                PMSOrganizationCustomizationActions.getTemplate(activeUid),
            );
        } else {
            dispatch(PMSCustomizationActions.getPMSCustomizationTemplate());
        }
    }, [dispatch, isResolving, activeUid]);

    const customization = activeUid
        ? storedOrganizationTemplate &&
          storedOrganizationTemplate.uid === activeUid
            ? storedOrganizationTemplate.customization
            : null
        : personalCustomization;
    const isUploading = activeUid
        ? organizationIsUploading
        : personalIsUploading;

    //ACTION HANDLERS (branch to the active scope)
    function handleChangeAccentColor(hex) {
        if (activeUid) {
            dispatch(
                PMSOrganizationCustomizationActions.update(activeUid, hex),
            );
        } else {
            dispatch(PMSCustomizationActions.update(hex));
        }
    }

    function handleChangeHeaderColor(hex) {
        if (activeUid) {
            dispatch(
                PMSOrganizationCustomizationActions.update(
                    activeUid,
                    undefined,
                    hex,
                ),
            );
        } else {
            dispatch(PMSCustomizationActions.update(undefined, hex));
        }
    }

    function handleResetAccentColor() {
        handleChangeAccentColor("");
    }

    function handleResetHeaderColor() {
        if (activeUid) {
            dispatch(
                PMSOrganizationCustomizationActions.update(
                    activeUid,
                    undefined,
                    "",
                ),
            );
        } else {
            dispatch(PMSCustomizationActions.update(undefined, ""));
        }
    }

    function handleUploadLogo(logo) {
        if (activeUid) {
            dispatch(
                PMSOrganizationCustomizationActions.uploadLogo(activeUid, logo),
            );
        } else {
            dispatch(PMSCustomizationActions.uploadLogo(logo));
        }
    }

    function handleRemoveLogo() {
        if (activeUid) {
            dispatch(PMSOrganizationCustomizationActions.removeLogo(activeUid));
        } else {
            dispatch(PMSCustomizationActions.removeLogo());
        }
    }

    return (
        <ContentSpace
            isEmbedded={isEmbedded}
            mainContent={
                <MainPageWithButtons
                    pageTitle={isEmbedded ? undefined : t("mod_pms_customization")}
                    content={content()}
                    isFlat={isEmbedded}
                />
            }
        />
    );

    function content() {
        // A failed organizations load must not leave the module spinning
        // forever (and silently editing the personal scope would be worse):
        // surface it with a retry, like PMSOrganizationDetail does.
        if (isResolving && hasFailed) return loadError();
        if (isResolving) return spinner();

        return (
            <div>
                <div className="mt-1" />
                {!isEmbedded && (
                    <PMSOrganizationScopeSelector
                        activeOrganization={activeOrganization}
                    />
                )}
                {hierarchyMessage()}
                <CustomizationTemplateEditor
                    customization={customization}
                    isUploading={isUploading}
                    onChangeAccentColor={handleChangeAccentColor}
                    onChangeHeaderColor={handleChangeHeaderColor}
                    onResetAccentColor={handleResetAccentColor}
                    onResetHeaderColor={handleResetHeaderColor}
                    onUploadLogo={handleUploadLogo}
                    onRemoveLogo={handleRemoveLogo}
                />
            </div>
        );
    }

    function hierarchyMessage() {
        return (
            <div className="alert alert-info mb-3" role="alert">
                {activeUid
                    ? t("mod_organizations_customization_note")
                    : t("mod_pms_customization_overridden_by_location")}
            </div>
        );
    }

    function loadError() {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-4">
                <h5 className="text-center">{t("global_error_network")}</h5>
                <div className="mt-3">
                    <CapsuleButton
                        text={t("global_retry")}
                        onClick={() =>
                            dispatch(OrganizationsActions.listOrganizations())
                        }
                    />
                </div>
            </div>
        );
    }

    function spinner() {
        return (
            <div className="w-100 pt-2 text-center">
                <div className="spinner-border c-text-primary" />
            </div>
        );
    }
}
